'use client'

import { FormEvent, useState } from 'react'
import { PollResponseVo } from '@schemas/non-auth'
import { pxToRem, pretendard } from '@ui/style/muiTheme'
import color from '@ui/style/color.theme'
import Box from '@mui/joy/Box'
import UIText from '@components/ui/UIText'
import UIFlex from '@components/ui/UIFlex'
import UIButton from '@components/ui/UIButton'
import { DATE_FORMAT, dayjs } from '@utils/date'
import { useUserTokenControl } from '@store/userToken'
import { useAuthControl } from '@store/auth'
import { usePollParticiapationMutation } from '@services/api/Poll/Particiapation/mutation'
import SubSectionTitle from '@page/article/_components/common/SubSectionTitle'
import useModal from '@components/layout/Modal'
import { useNotification } from '@lib/notification'
import { useJtbcCommentMutation } from '@services/api/Poll/JtbcComment/mutation'
import PollRadioSet from './poll/PollRadioSet'
import PollResultBarSet from './result/PollResultBarSet'
import PollCheckSet from './poll/PollCheckSet'
import PollOxSet from './poll/PollOxSet'
import PollYesNoSet from './poll/PollYesNoSet'
import PollResultPieSet from './result/PollResultPieSet'
import { AreaDiv } from '@services/api/Statistics/types'
import { useStatisticsSelectionAreaMutation } from '@services/api/Statistics/SelectionArea/mutation'
import { useRouter } from 'next/navigation'
import nProgress from 'nprogress'
import ContentBadge from '@components/content/ContentBadge'
import { useAuth } from '@hooks/useAuth'

export interface PollBoxProps {
	poll: PollResponseVo
	areaDiv?: AreaDiv
	areaIdx: number
	onSubmit?: () => void
}

const PollBox = ({ poll, areaDiv, areaIdx, onSubmit }: PollBoxProps) => {
	const router = useRouter()
	const { isLogin } = useAuth()
	const { auth } = useAuthControl()
	const { notify } = useNotification()
	const { userToken } = useUserTokenControl()
	const [selected, setSelected] = useState<string[]>([])
	const [answer, setAnswer] = useState<string>()
	const [isParticipation, setIsParticipation] = useState(() =>
		isLogin ? false : localStorage.getItem(`poll-${poll.pollIdx}-isParticipation`),
	)
	const confirm = useModal()
	const { mutateAsync: jtbcCommentMutate } = useJtbcCommentMutation()
	const { mutateAsync: pollParticiapationMutate } = usePollParticiapationMutation()
	const { mutateAsync: selectionAreaMutation } = useStatisticsSelectionAreaMutation()

	const authAction = () => {
		confirm.login()
	}
	const handleActive = () => {
		const selectedAnswer = poll.answerList.find((item) =>
			selected.includes(item.answerIdx.toString()),
		)
		return (
			!selectedAnswer ||
			(selectedAnswer?.answerTypeCode.code === 'SUBJECTIVE' && (answer?.length ?? 0) === 0)
		)
	}
	const selectionAreaAction = async () => {
		await selectionAreaMutation({ areaDiv, areaIdx: areaIdx.toString() })
	}
	const pushComment = async (close: () => void) => {
		const response = await jtbcCommentMutate({
			pollIdx: poll?.pollIdx,
			answerContent: answer as string,
		})
		if (response?.data) {
			close()
			notify('내 의견이 Poll 댓글로 등록되었습니다.')
			window.dispatchEvent(new CustomEvent('refreshComment', {}))
		}
	}
	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		e.stopPropagation()

		if (poll?.participationTargetCode?.code === 'MEMBER-ONLY' && !auth) {
			authAction()
		} else if (selected.length > 0) {
			const response = await pollParticiapationMutate({
				pollIdx: poll?.pollIdx,
				answerIdxs: selected,
				answer,
				nonMemberId: userToken,
			})
			if (response.resultCode === '10') {
				notify(response.resultMessage)
			} else if (response?.data) {
				localStorage.setItem(`poll-${poll.pollIdx}-isParticipation`, 'true')
				setIsParticipation(true)
				onSubmit?.()
				if (answer && auth && poll.isCommentUse) {
					confirm.open({
						message:
							'참여 완료하였습니다.\n작성한 의견을 Poll 댓글로\n등록하시겠습니까?',
						buttons: [
							{ text: '아니요' },
							{
								text: '등록',
								onClick: (close) => {
									pushComment(close)
								},
							},
						],
					})
				} else {
					notify('참여 완료하였습니다.')
				}
				selectionAreaAction()
			}
		}
	}
	const handleDetail = async () => {
		await selectionAreaAction()
		nProgress.start()
		router.push(`/poll/${poll?.pollIdx}`)
	}
	const today = dayjs()
	const isStarted = dayjs(poll.pollStartDate).diff(today, 'dates') < 0
	const isEnded = dayjs(poll.pollEndDate).diff(today, 'dates') < 0
	return (
		isStarted && (
			<Box
				component="form"
				onSubmit={handleSubmit}
				padding={`${pxToRem(14)} ${pxToRem(20)} ${pxToRem(20)} ${pxToRem(20)}`}
				borderRadius={pxToRem(8)}
				sx={{
					background: color.colGray1,
				}}
			>
				<SubSectionTitle
					rightSlot={
						poll?.pollStartDate &&
						poll?.pollEndDate && (
							<UIText
								color={color.colGray5}
								level="Footnote"
								sx={{ fontFamily: pretendard.style.fontFamily }}
							>
								{dayjs(poll.pollStartDate).format(DATE_FORMAT.DATE)} ~{' '}
								{dayjs(poll.pollEndDate).format(DATE_FORMAT.DATE)}
							</UIText>
						)
					}
					pb={pxToRem(12)}
					sx={{
						flexWrap: 'wrap',
						'&>.MuiTypography-root': {
							display: 'flex',
							alignItems: 'center',
							'&>*:not(:first-child)': {
								marginLeft: pxToRem(12),
							},
						},
					}}
				>
					<UIText>Poll</UIText>
					{poll?.participationTargetCode.code === 'MEMBER-ONLY' && (
						<ContentBadge contentType="EXCLUSIVE" />
					)}
				</SubSectionTitle>
				<UIFlex
					pt={pxToRem(16)}
					borderTop={`1px solid ${color.colGray3}`}
					sx={{
						'&>*:not(:first-child)': {
							marginTop: pxToRem(16),
						},
					}}
				>
					<UIText
						fontSize={pxToRem(16)}
						lineHeight={pxToRem(24)}
						fontWeight={700}
						sx={{ wordBreak: 'break-all' }}
					>
						{poll?.question}
					</UIText>
					{isEnded || (isParticipation ?? poll.isPollParticipation) ? (
						<>
							{['MULTIPLE_CHOICE_SINGULAR', 'MULTIPLE_CHOICE_PLURAL'].includes(
								poll.pollTypeCode.code as string,
							) && <PollResultBarSet poll={poll} selected={selected} />}
							{['AGREE_DISSENT', 'OX'].includes(poll.pollTypeCode.code as string) && (
								<PollResultPieSet poll={poll} selected={selected} />
							)}
						</>
					) : (
						<>
							{poll.pollTypeCode.code === 'MULTIPLE_CHOICE_SINGULAR' && (
								<PollRadioSet
									poll={poll}
									auth={auth}
									selected={selected}
									setSelected={setSelected}
									setAnswer={setAnswer}
									authAction={authAction}
								/>
							)}
							{poll.pollTypeCode.code === 'MULTIPLE_CHOICE_PLURAL' && (
								<PollCheckSet
									poll={poll}
									auth={auth}
									selected={selected}
									setSelected={setSelected}
									setAnswer={setAnswer}
									authAction={authAction}
								/>
							)}
							{poll.pollTypeCode.code === 'OX' && (
								<PollOxSet
									poll={poll}
									auth={auth}
									selected={selected}
									setSelected={setSelected}
									authAction={authAction}
								/>
							)}
							{poll.pollTypeCode.code === 'AGREE_DISSENT' && (
								<PollYesNoSet
									poll={poll}
									auth={auth}
									selected={selected}
									setSelected={setSelected}
									authAction={authAction}
								/>
							)}
						</>
					)}
				</UIFlex>
				<UIFlex mt={pxToRem(20)} alignItems="center">
					{isEnded || (isParticipation ?? poll?.isPollParticipation) ? (
						<UIButton
							variant="outlined"
							color="neutral"
							onClick={handleDetail}
							sx={{
								'&.MuiButton-root': {
									padding: `${pxToRem(11)} ${pxToRem(15)}`,
									borderColor: color.colBlack,
									width: pxToRem(96),
									height: pxToRem(40),
								},
							}}
						>
							자세히 보기
						</UIButton>
					) : (
						<UIButton
							type="submit"
							variant="contained"
							color="primary"
							disabled={handleActive()}
							sx={{
								'&.MuiButton-root': {
									padding: `${pxToRem(12)} ${pxToRem(16)}`,
									width: pxToRem(85),
									height: pxToRem(40),
								},
							}}
						>
							참여하기
						</UIButton>
					)}
				</UIFlex>
			</Box>
		)
	)
}

export default PollBox
